// src/components/PipelinePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
// P0.31 31.B.5 — the Archived view. 89 of this tenant's 133 opportunities were
// archived and reachable from no screen.
import useArchivedView from '../hooks/useArchivedView';
import { confirmArchive } from '../lib/archiveConfirm';
import './PipelinePage.css';
import DataTransferMenu from './DataTransferMenu';

// Stage vocabulary imported from canonical source (Workstream C — Cadence Engine Foundation)
import { STAGES, ACTIVE_STAGES, CLOSED_STAGES, STAGE_CONFIG } from '../config/stages';

const PRIORITY_CONFIG = {
  High:   { color: 'var(--dm-red)', bg: 'var(--dm-red-light)' },
  Medium: { color: '#F97316', bg: '#FFF7ED' },
  Low:    { color: 'var(--dm-grey-600)', bg: '#F3F4F6' },
};

const MEDDIC_SCORE_CONFIG = {
  '🔴 Weak — 1-2 elements':       { color: '#991B1B', bg: 'var(--dm-red-light)', short: '🔴 Weak'       },
  '🟡 Developing — 3-4 elements': { color: '#92400E', bg: '#FEF9C3', short: '🟡 Developing'  },
  '🟢 Strong — 5-6 elements':     { color: 'var(--dm-green-deep)', bg: 'var(--dm-green-wash)', short: '🟢 Strong'       },
};

const IMPACT_ICON = { Speed: '⚡', Risk: '🛡️', Growth: '📈', Cost: '💰' };

const EMPTY_FORM = {
  name: '', priority: 'High', dealSize: '',
  probability: '10% - Prospecting', impactArea: '',
  competitiveSensitivity: '', expectedClose: '',
  valueHypothesis: '', nextMilestone: '',
  competitiveThreats: '', lossReason: '', meddicScore: '',
  meddicMetrics: '', meddicEconomicBuyer: '', meddicDecisionCriteria: '',
  meddicDecisionProcess: '', meddicIdentifiedPain: '', meddicChampion: '',
};

function formatDealSize(val) {
  const n = Number(val);
  if (isNaN(n) || val === '' || val == null) return '-';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 2)}M`;
  if (n >= 1000)    return `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `$${n.toLocaleString()}`;
}

function meddicCompletionCount(opp) {
  return [
    opp.meddicMetrics, opp.meddicEconomicBuyer, opp.meddicDecisionCriteria,
    opp.meddicDecisionProcess, opp.meddicIdentifiedPain, opp.meddicChampion,
  ].filter(v => v && v.trim()).length;
}

// ── MEDDIC collapsible section ───────────────────────────────────────────────
function MeddicSection({ opp, isForm = false, formData, onFormChange }) {
  const [open, setOpen] = useState(false);

  const completed = isForm
    ? [formData.meddicMetrics, formData.meddicEconomicBuyer, formData.meddicDecisionCriteria,
       formData.meddicDecisionProcess, formData.meddicIdentifiedPain, formData.meddicChampion]
       .filter(v => v && v.trim()).length
    : meddicCompletionCount(opp || {});

  const elements = [
    { key: 'meddicMetrics',           label: 'Metrics',           icon: '📊', hint: 'What quantified business impact justifies this project?' },
    { key: 'meddicEconomicBuyer',     label: 'Economic Buyer',    icon: '💼', hint: 'Who controls the budget and has final signing authority?' },
    { key: 'meddicDecisionCriteria',  label: 'Decision Criteria', icon: '📋', hint: 'How will they evaluate and select a vendor?' },
    { key: 'meddicDecisionProcess',   label: 'Decision Process',  icon: '🗺️', hint: 'What are the steps from now to a signed contract?' },
    { key: 'meddicIdentifiedPain',    label: 'Identified Pain',   icon: '🎯', hint: 'What is the core problem creating urgency to act?' },
    { key: 'meddicChampion',          label: 'Champion',          icon: '🏆', hint: 'Who inside the account is fighting for us?' },
  ];

  const scoreColor = completed >= 5 ? 'var(--dm-green-deep)' : completed >= 3 ? '#92400E' : '#991B1B';
  const scoreBg    = completed >= 5 ? 'var(--dm-green-wash)' : completed >= 3 ? '#FEF9C3' : 'var(--dm-red-light)';

  return (
    <div className="meddic-section">
      <button className="meddic-toggle" onClick={() => setOpen(!open)}>
        <div className="meddic-toggle-left">
          <span className="meddic-label">MEDDIC Pursuit Framework</span>
          <span className="meddic-score-badge" style={{ background: scoreBg, color: scoreColor }}>
            {completed}/6 Complete
          </span>
        </div>
        <span className="meddic-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="meddic-body">
          {/* Progress bar */}
          <div className="meddic-progress-bar">
            <div
              className="meddic-progress-fill"
              style={{
                width: `${(completed / 6) * 100}%`,
                background: scoreColor,
              }}
            />
          </div>

          <div className="meddic-grid">
            {elements.map(el => {
              const value = isForm ? (formData[el.key] || '') : ((opp || {})[el.key] || '');
              const filled = value.trim().length > 0;
              return (
                <div key={el.key} className={`meddic-element ${filled ? 'filled' : 'empty'}`}>
                  <div className="meddic-element-header">
                    <span className="meddic-icon">{el.icon}</span>
                    <span className="meddic-element-label">{el.label}</span>
                    {filled && <span className="meddic-check">✓</span>}
                  </div>
                  {isForm ? (
                    <textarea
                      className="meddic-textarea"
                      value={value}
                      onChange={e => onFormChange(el.key, e.target.value)}
                      placeholder={el.hint}
                      rows={2}
                    />
                  ) : (
                    <p className="meddic-value">{value || <span className="meddic-empty-hint">{el.hint}</span>}</p>
                  )}
                </div>
              );
            })}
          </div>

          {isForm && (
            <div className="meddic-score-select-row">
              <label>MEDDIC Score Override</label>
              <select
                value={formData.meddicScore || ''}
                onChange={e => onFormChange('meddicScore', e.target.value)}
              >
                <option value="">Auto (based on completion)</option>
                <option value="🔴 Weak — 1-2 elements">🔴 Weak — 1-2 elements</option>
                <option value="🟡 Developing — 3-4 elements">🟡 Developing — 3-4 elements</option>
                <option value="🟢 Strong — 5-6 elements">🟢 Strong — 5-6 elements</option>
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
function PipelinePage({ onNavigate }) {
  const [opportunities, setOpportunities]       = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [selectedOpp, setSelectedOpp]           = useState(null);
  const [modalMode, setModalMode]               = useState('view');
  const [formData, setFormData]                 = useState(EMPTY_FORM);
  const [saving, setSaving]                     = useState(false);
  const [deleting, setDeleting]                 = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage]     = useState('');
  const [filterStage, setFilterStage]           = useState('active');
  const [filterPriority, setFilterPriority]     = useState('all');
  const [search, setSearch]                     = useState('');

  const archive = useArchivedView();

  const archiveOpportunity = async (opp) => {
    if (!confirmArchive({ entity: 'opportunity', name: opp.name })) return;
    try {
      const res = await apiFetch('/api/opportunities/delete', {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opp.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to archive');
      setOpportunities(prev => prev.filter(o => o.id !== opp.id));
    } catch (err) { alert('Could not archive that opportunity: ' + err.message); }
  };

  const restoreOpportunity = async (opp) => {
    try {
      const res = await apiFetch('/api/opportunities/unarchive', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opp.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to restore');
      setOpportunities(prev => prev.filter(o => o.id !== opp.id));
    } catch (err) { alert('Could not restore that opportunity: ' + err.message); }
  };

  const fetchOpportunities = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await apiFetch('/api/opportunities/list?' + archive.param.replace(/^&/, ''));
      const data = await res.json();
      if (data.success) {
        const normalized = (data.opportunities || []).map(o => ({
          id:                     o.id,
          // P0.31 — carried through the re-normalisation, or the Archived view has
          // nothing to partition on and every row looks active.
          deletedAt:              o.deletedAt || o.deleted_at || null,
          url:                    o.url,
          name:                   o.opportunity || o.name || '',
          status:                 o.status       || '',
          priority:               o.priority     || '',
          dealSize:               o.deal_size    ?? o.dealSize    ?? '',
          probability:            o.probability  || '',
          impactArea:             o.impact_area  || o.impactArea  || '',
          competitiveSensitivity: o.competitive_sensitivity || o.competitiveSensitivity || '',
          expectedClose:          o.expected_close || o.expectedClose || '',
          valueHypothesis:        o.value_hypothesis || o.valueHypothesis || '',
          accountName:            o.accountName  || '',
          accountId:              o.account_id    || null,  // API returns snake_case (was o.account?.[0] from Notion era)
          // New fields
          nextMilestone:          o.next_milestone         || '',
          competitiveThreats:     o.competitive_threats    || '',
          lossReason:             o.loss_reason            || '',
          meddicScore:            o.meddic_score           || '',
          meddicMetrics:          o.meddic_metrics         || '',
          meddicEconomicBuyer:    o.meddic_economic_buyer  || '',
          meddicDecisionCriteria: o.meddic_decision_criteria || '',
          meddicDecisionProcess:  o.meddic_decision_process  || '',
          meddicIdentifiedPain:   o.meddic_identified_pain   || '',
          meddicChampion:         o.meddic_champion          || '',
        }));
        setOpportunities(normalized);
      } else {
        setError(data.error || 'Failed to load');
      }
    } catch (err) {
      setError('Failed to load opportunities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  // `archive.param` IS A DEPENDENCY. Left as [] the callback closes over the
  // first render's value, so toggling the Archived view refetches the same list
  // and nothing changes on screen — which reads as a broken button rather than a
  // stale closure. That bug shipped in SignalsPage earlier in this phase.
  }, [archive.param]);

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  const showToast = (msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); };

  const handleCardClick = (opp) => {
    // Navigate to the account detail page with the opportunity pre-selected.
    // Reads opp.accountId — the internal normalized shape produced by
    // fetchOpportunities() above. The normalizer maps API's account_id to
    // accountId (line ~210). Keep this consistent: if the normalizer changes,
    // this read changes too.
    if (opp.accountId) {
      if (onNavigate) {
        onNavigate('account-detail', opp.accountId, { openOpportunityId: opp.id });
      } else if (window.__dmNavigate) {
        window.__dmNavigate('account-detail', opp.accountId, { openOpportunityId: opp.id });
      } else {
        setSelectedOpp(opp); setModalMode('view');
      }
    } else {
      setSelectedOpp(opp); setModalMode('view');
    }
  };

  const handleEditClick = (opp) => {
    setFormData({
      name:                   opp.name                   || '',
      priority:               opp.priority               || 'High',
      dealSize:               opp.dealSize               || '',
      probability:            opp.probability            || '10% - Prospecting',
      impactArea:             opp.impactArea             || '',
      competitiveSensitivity: opp.competitiveSensitivity || '',
      expectedClose:          opp.expectedClose          || '',
      valueHypothesis:        opp.valueHypothesis        || '',
      nextMilestone:          opp.nextMilestone          || '',
      competitiveThreats:     opp.competitiveThreats     || '',
      lossReason:             opp.lossReason             || '',
      meddicScore:            opp.meddicScore            || '',
      meddicMetrics:          opp.meddicMetrics          || '',
      meddicEconomicBuyer:    opp.meddicEconomicBuyer    || '',
      meddicDecisionCriteria: opp.meddicDecisionCriteria || '',
      meddicDecisionProcess:  opp.meddicDecisionProcess  || '',
      meddicIdentifiedPain:   opp.meddicIdentifiedPain   || '',
      meddicChampion:         opp.meddicChampion         || '',
    });
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setSelectedOpp(null); setModalMode('view');
    setFormData(EMPTY_FORM); setSuccessMessage(''); setShowDeleteConfirm(false);
  };

  const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const res  = await apiFetch('/api/opportunities/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedOpp.id, ...formData }),
      });
      const data = await res.json();
      if (data.success) {
        await fetchOpportunities();
        showToast(`✅ "${formData.name}" updated!`);
        handleCloseModal();
      } else { alert('Failed to update: ' + data.error); }
    } catch (err) { alert('Error saving'); console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedOpp) return;
    setDeleting(true);
    try {
      const res  = await apiFetch(`/api/opportunities/delete?id=${selectedOpp.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await fetchOpportunities();
        showToast(`🗑️ "${selectedOpp.name}" deleted`);
        handleCloseModal();
      } else { alert('Failed to delete: ' + data.error); }
    } catch (err) { alert('Error deleting'); console.error(err); }
    finally { setDeleting(false); }
  };

  // ── Filter logic ──────────────────────────────────────────────────────────
  const isActive = (o) => ACTIVE_STAGES.includes(o.probability);

  const filtered = opportunities.filter(o => {
    if (filterStage === 'active' && !isActive(o)) return false;
    if (filterStage !== 'active' && filterStage !== 'all' && o.probability !== filterStage) return false;
    if (filterPriority !== 'all' && o.priority !== filterPriority) return false;
    if (search && !(o.name || '').toLowerCase().includes(search.toLowerCase()) &&
        !(o.accountName || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = STAGES.reduce((acc, stage) => {
    acc[stage] = filtered.filter(o => o.probability === stage);
    return acc;
  }, {});

  const activeOpps   = opportunities.filter(o => isActive(o) && o.probability !== 'Under Review');
  const underReview  = opportunities.filter(o => o.probability === 'Under Review');
  const totalTCV     = activeOpps.reduce((sum, o) => sum + (Number(o.dealSize) || 0), 0);
  const reviewTCV    = underReview.reduce((sum, o) => sum + (Number(o.dealSize) || 0), 0);
  const displayStages = filterStage === 'active' ? ACTIVE_STAGES : STAGES;

  const isClosedStage = (prob) => CLOSED_STAGES.includes(prob);

  if (loading) return (
    <div className="pipeline-page">
      <div className="opp-loading"><p>Loading pipeline...</p></div>
    </div>
  );

  return (
    <div className="pipeline-page">
      {successMessage && <div className="opp-toast">{successMessage}</div>}

      {/* Header */}
      <div className="pipeline-header">
        <div className="pipeline-header-left">
          <div className="pipeline-title-row">
            <h1 className="pipeline-title">Pipeline</h1>
            {activeOpps.length > 0 && <span className="pipeline-count">{activeOpps.length}</span>}
            {totalTCV > 0 && (
              <span className="pipeline-tcv">{formatDealSize(totalTCV)} TCV</span>
            )}
            {underReview.length > 0 && (
              <span className="pipeline-under-review">
                · Under Review: {underReview.length}{reviewTCV > 0 ? ` · ${formatDealSize(reviewTCV)}` : ''}
              </span>
            )}
          </div>
          <div className="pipeline-stats">
            {ACTIVE_STAGES.filter(s => s !== 'Under Review').map(stage => {
              const count = opportunities.filter(o => o.probability === stage).length;
              if (!count) return null;
              const cfg = STAGE_CONFIG[stage];
              return (
                <span key={stage} className="pipeline-stat-chip" style={{ background: cfg.bg, color: cfg.color }}>
                  {cfg.icon} {cfg.label}: {count}
                </span>
              );
            })}
          </div>
        </div>
        <div className="pipeline-header-right">
          <DataTransferMenu entity="opportunities" label="Pipeline" onImported={fetchOpportunities} />
          {/* P0.31 31.B.5 — the Archived view. */}
          <button className={`btn-refresh${archive.showArchived ? ' btn-refresh-on' : ''}`}
                  onClick={archive.toggle} title={archive.button.title}>
            {archive.button.label}
          </button>
          <button className="btn-refresh" onClick={fetchOpportunities}>↻ Refresh</button>
        </div>
      </div>

      {/* Filters */}
      <div className="pipeline-filters">
        <input
          className="search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search opportunities or accounts..."
        />
        <select className="filter-select" value={filterStage} onChange={e => setFilterStage(e.target.value)}>
          <option value="active">Active Pipeline</option>
          <option value="all">All Stages</option>
          {STAGES.map(s => (
            <option key={s} value={s}>{STAGE_CONFIG[s].icon} {STAGE_CONFIG[s].label}</option>
          ))}
        </select>
        <select className="filter-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="all">All Priorities</option>
          <option>High</option><option>Medium</option><option>Low</option>
        </select>
      </div>

      {error && (
        <div className="opp-error">
          ⚠️ {error} <button onClick={fetchOpportunities} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Pipeline Board */}
      {filtered.length === 0 && !error ? (
        <div className="opp-empty">
          <p>{opportunities.length === 0 ? 'No opportunities in your pipeline yet.' : 'No opportunities match your filters.'}</p>
          {opportunities.length === 0 && (
            <p className="pipeline-empty-hint">Convert leads to opportunities from the Leads page.</p>
          )}
        </div>
      ) : (
        <div className="opp-pipeline">
          {displayStages.map(stage => {
            const config    = STAGE_CONFIG[stage];
            const stageOpps = grouped[stage] || [];
            if (stageOpps.length === 0 && filterStage === 'active') return null;
            return (
              <div key={stage} className="opp-stage-column">
                <div className="opp-stage-header" style={{ borderTopColor: config.color }}>
                  <span className="opp-stage-icon">{config.icon}</span>
                  <span className="opp-stage-label">{config.label}</span>
                  <span className="opp-stage-badge" style={{ background: config.bg, color: config.color }}>
                    {stageOpps.length}
                  </span>
                </div>

                <div className="opp-cards">
                  {stageOpps.map(opp => {
                    const mc     = meddicCompletionCount(opp);
                    const mScore = opp.meddicScore;
                    const mCfg   = MEDDIC_SCORE_CONFIG[mScore] || null;
                    const mColor = mc >= 5 ? 'var(--dm-green-deep)' : mc >= 3 ? '#92400E' : 'var(--dm-grey-600)';
                    const mBg    = mc >= 5 ? 'var(--dm-green-wash)' : mc >= 3 ? '#FEF9C3' : '#F3F4F6';

                    return (
                      <div key={opp.id} className="opp-card" onClick={() => handleCardClick(opp)}>
                        {/* P0.31 31.B.3/31.B.5 — archive and restore. 89 of 133
                            opportunities in this tenant were archived and
                            reachable from nowhere before this. */}
                        <button className="opp-archive-btn"
                                onClick={e => { e.stopPropagation();
                                  (opp.deletedAt ? restoreOpportunity : archiveOpportunity)(opp); }}
                                title={opp.deletedAt ? 'Restore' : 'Archive'}>
                          {opp.deletedAt ? '↩' : '✕'}
                        </button>

                        {/* Header: Account · Opportunity Name + Priority */}
                        <div className="opp-card-header" style={{ marginBottom: '2px' }}>
                          <div className="opp-card-title-block" style={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: 1, minWidth: 0 }}>
                            {opp.accountName && (
                              <span className="opp-card-account-inline" style={{ fontSize: '10px', color: '#64748b', fontWeight: 500 }}>🏢 {opp.accountName}</span>
                            )}
                            <h4 className="opp-card-title" style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#1e2d5a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opp.name}</h4>
                          </div>
                          {opp.priority && (
                            <span className="opp-card-priority" style={{
                              background: PRIORITY_CONFIG[opp.priority]?.bg,
                              color:      PRIORITY_CONFIG[opp.priority]?.color,
                            }}>
                              {opp.priority}
                            </span>
                          )}
                        </div>

                        {/* MEDDIC Score Pill — directly under name */}
                        <div className="opp-card-meddic-row" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0 6px 0', borderBottom: '1px solid #f0f4f8', marginBottom: '8px' }}>
                          <span
                            className="opp-meddic-pill"
                            style={{ background: mCfg ? mCfg.bg : mBg, color: mCfg ? mCfg.color : mColor }}
                            title="MEDDIC completion"
                          >
                            {mCfg ? mCfg.short : `MEDDIC ${mc}/6`}
                          </span>
                          {opp.impactArea && (
                            <span className="opp-impact-chip">
                              {IMPACT_ICON[opp.impactArea]} {opp.impactArea}
                            </span>
                          )}
                        </div>

                        {/* 2-column detail grid */}
                        <div className="opp-card-grid">
                          <div className="opp-card-grid-item">
                            <span className="opp-card-label">Stage</span>
                            <span className="opp-card-value" style={{
                              color: STAGE_CONFIG[opp.probability]?.color,
                              fontSize: '10px', fontWeight: 600,
                            }}>
                              {STAGE_CONFIG[opp.probability]?.icon} {STAGE_CONFIG[opp.probability]?.label || '—'}
                            </span>
                          </div>
                          <div className="opp-card-grid-item">
                            <span className="opp-card-label">Priority</span>
                            <span className="opp-card-value" style={{ color: PRIORITY_CONFIG[opp.priority]?.color }}>
                              {opp.priority || '—'}
                            </span>
                          </div>
                          <div className="opp-card-grid-item">
                            <span className="opp-card-label">Deal Size</span>
                            <span className="opp-card-value opp-deal-size">
                              {opp.dealSize != null && opp.dealSize !== '' ? formatDealSize(opp.dealSize) : '—'}
                            </span>
                          </div>
                          <div className="opp-card-grid-item">
                            <span className="opp-card-label">Expected Close</span>
                            <span className="opp-card-value">{opp.expectedClose || '—'}</span>
                          </div>
                          <div className="opp-card-grid-item">
                            <span className="opp-card-label">Impact Area</span>
                            <span className="opp-card-value">
                              {opp.impactArea ? `${IMPACT_ICON[opp.impactArea]} ${opp.impactArea}` : '—'}
                            </span>
                          </div>
                          <div className="opp-card-grid-item">
                            <span className="opp-card-label">Competitive</span>
                            <span className="opp-card-value">{opp.competitiveSensitivity || '—'}</span>
                          </div>
                        </div>

                        {/* Value Hypothesis — full width */}
                        {opp.valueHypothesis && (
                          <div className="opp-card-hypothesis">
                            <span className="opp-card-label">Value Hypothesis</span>
                            <p className="opp-card-hypothesis-text">{opp.valueHypothesis}</p>
                          </div>
                        )}

                        {/* Next Milestone */}
                        {opp.nextMilestone && (
                          <div className="opp-card-next-milestone">🎯 {opp.nextMilestone}</div>
                        )}

                        {/* Victor + Cyrano quick-launch buttons */}
                        {window.__dmNavigate && (
                          <div className="opp-card-ai-bar" onClick={e => e.stopPropagation()}>
                            <button
                              className="opp-ai-btn opp-ai-btn--victor"
                              title="Open in Victor Strategy Studio"
                              onClick={() => window.__dmNavigate('victor', null, {
                                accountId:       opp.accountId,
                                accountName:     opp.accountName,
                                opportunityId:   opp.id,
                                opportunityName: opp.name,
                                mode:            'deal_coach',
                              })}
                            >
                              ⚡ Victor
                            </button>
                            <button
                              className="opp-ai-btn opp-ai-btn--cyrano"
                              title="Open in Cyrano Comms Studio"
                              onClick={() => window.__dmNavigate('content', null, {
                                accountId:       opp.accountId,
                                accountName:     opp.accountName,
                                opportunityId:   opp.id,
                                opportunityName: opp.name,
                              })}
                            >
                              ✍️ Cyrano
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {selectedOpp && (
        <div className="opp-modal-overlay" onClick={handleCloseModal}>
          <div className="opp-modal opp-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="opp-modal-header">
              <h2 className="opp-modal-title">
                {modalMode === 'edit' ? '✏️ Edit Opportunity' : '📊 Opportunity Details'}
              </h2>
              <button className="opp-modal-close" onClick={handleCloseModal}>✕</button>
            </div>

            <div className="opp-modal-content">
              {modalMode === 'view' ? (
                <>
                  {/* Core info grid */}
                  <div className="opp-view-grid">
                    <div className="opp-view-field full">
                      <label>Opportunity</label>
                      <p className="opp-view-value opp-name-large">{selectedOpp.name}</p>
                    </div>
                    {selectedOpp.accountName && (
                      <div className="opp-view-field full">
                        <label>Account</label>
                        <p className="opp-view-value">🏢 {selectedOpp.accountName}</p>
                      </div>
                    )}
                    {/* MEDDIC directly under opportunity name */}
                    <div className="opp-view-field full" style={{ paddingTop: 0 }}>
                      <MeddicSection opp={selectedOpp} isForm={false} />
                    </div>
                    <div className="opp-view-field">
                      <label>Stage</label>
                      <span className="opp-badge" style={{
                        background: STAGE_CONFIG[selectedOpp.probability]?.bg,
                        color:      STAGE_CONFIG[selectedOpp.probability]?.color,
                      }}>
                        {STAGE_CONFIG[selectedOpp.probability]?.icon} {STAGE_CONFIG[selectedOpp.probability]?.label || selectedOpp.probability}
                      </span>
                    </div>
                    <div className="opp-view-field">
                      <label>Priority</label>
                      <span className="opp-badge" style={{
                        background: PRIORITY_CONFIG[selectedOpp.priority]?.bg,
                        color:      PRIORITY_CONFIG[selectedOpp.priority]?.color,
                      }}>
                        {selectedOpp.priority || '-'}
                      </span>
                    </div>
                    <div className="opp-view-field">
                      <label>Deal Size</label>
                      <p className="opp-view-value opp-deal-big">{formatDealSize(selectedOpp.dealSize)}</p>
                    </div>
                    <div className="opp-view-field">
                      <label>Expected Close</label>
                      <p className="opp-view-value">{selectedOpp.expectedClose || '-'}</p>
                    </div>
                    <div className="opp-view-field">
                      <label>Impact Area</label>
                      <p className="opp-view-value">
                        {selectedOpp.impactArea ? `${IMPACT_ICON[selectedOpp.impactArea]} ${selectedOpp.impactArea}` : '-'}
                      </p>
                    </div>
                    <div className="opp-view-field">
                      <label>Competitive Sensitivity</label>
                      <p className="opp-view-value">{selectedOpp.competitiveSensitivity || '-'}</p>
                    </div>
                    {selectedOpp.nextMilestone && (
                      <div className="opp-view-field full">
                        <label>🎯 Next Milestone</label>
                        <p className="opp-view-value opp-milestone">{selectedOpp.nextMilestone}</p>
                      </div>
                    )}
                    {selectedOpp.valueHypothesis && (
                      <div className="opp-view-field full">
                        <label>Value Hypothesis</label>
                        <p className="opp-view-text">{selectedOpp.valueHypothesis}</p>
                      </div>
                    )}
                    {selectedOpp.competitiveThreats && (
                      <div className="opp-view-field full">
                        <label>Competitive Threats</label>
                        <p className="opp-view-text">{selectedOpp.competitiveThreats}</p>
                      </div>
                    )}
                    {isClosedStage(selectedOpp.probability) && selectedOpp.lossReason && (
                      <div className="opp-view-field full">
                        <label>Loss / Close Reason</label>
                        <span className="opp-badge" style={{ background: 'var(--dm-red-light)', color: '#991B1B' }}>
                          {selectedOpp.lossReason}
                        </span>
                      </div>
                    )}
                  </div>


                </>
              ) : (
                /* EDIT FORM */
                <>
                  <div className="opp-form-grid">
                    <div className="opp-form-field full">
                      <label>Opportunity Name *</label>
                      <input type="text" value={formData.name}
                        onChange={e => handleFormChange('name', e.target.value)}
                        placeholder="e.g., GW Cloud Migration — Phase 1" />
                    </div>
                    <div className="opp-form-field">
                      <label>Stage *</label>
                      <select value={formData.probability} onChange={e => handleFormChange('probability', e.target.value)}>
                        {STAGES.map(stage => (
                          <option key={stage} value={stage}>
                            {STAGE_CONFIG[stage].icon} {STAGE_CONFIG[stage].label}
                            {STAGE_CONFIG[stage].pct != null ? ` (${STAGE_CONFIG[stage].pct}%)` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="opp-form-field">
                      <label>Priority</label>
                      <select value={formData.priority} onChange={e => handleFormChange('priority', e.target.value)}>
                        <option value="">Select...</option>
                        <option>High</option><option>Medium</option><option>Low</option>
                      </select>
                    </div>
                    <div className="opp-form-field">
                      <label>Deal Size ($)</label>
                      <input type="number" min="0" step="10000" value={formData.dealSize || ''}
                        onChange={e => handleFormChange('dealSize', e.target.value)} placeholder="e.g. 450000" />
                      {formData.dealSize && !isNaN(Number(formData.dealSize)) && (
                        <span className="opp-deal-preview">{formatDealSize(formData.dealSize)}</span>
                      )}
                    </div>
                    <div className="opp-form-field">
                      <label>Expected Close</label>
                      <input type="date" value={formData.expectedClose}
                        onChange={e => handleFormChange('expectedClose', e.target.value)} />
                    </div>
                    <div className="opp-form-field">
                      <label>Impact Area</label>
                      <select value={formData.impactArea} onChange={e => handleFormChange('impactArea', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="Speed">⚡ Speed</option>
                        <option value="Risk">🛡️ Risk</option>
                        <option value="Growth">📈 Growth</option>
                        <option value="Cost">💰 Cost</option>
                      </select>
                    </div>
                    <div className="opp-form-field">
                      <label>Competitive Sensitivity</label>
                      <select value={formData.competitiveSensitivity} onChange={e => handleFormChange('competitiveSensitivity', e.target.value)}>
                        <option value="">Select...</option>
                        <option>High</option><option>Medium</option><option>Low</option>
                      </select>
                    </div>
                    <div className="opp-form-field full">
                      <label>🎯 Next Milestone</label>
                      <input type="text" value={formData.nextMilestone}
                        onChange={e => handleFormChange('nextMilestone', e.target.value)}
                        placeholder="e.g., Submit proposal by March 20" />
                    </div>
                    <div className="opp-form-field full">
                      <label>Value Hypothesis</label>
                      <textarea value={formData.valueHypothesis}
                        onChange={e => handleFormChange('valueHypothesis', e.target.value)}
                        rows={3} placeholder="Describe the value proposition..." />
                    </div>
                    <div className="opp-form-field full">
                      <label>Competitive Threats</label>
                      <textarea value={formData.competitiveThreats}
                        onChange={e => handleFormChange('competitiveThreats', e.target.value)}
                        rows={2} placeholder="Who else is in the deal? Deloitte, Accenture, etc." />
                    </div>
                    {isClosedStage(formData.probability) && (
                      <div className="opp-form-field full">
                        <label>Loss / Close Reason</label>
                        <select value={formData.lossReason} onChange={e => handleFormChange('lossReason', e.target.value)}>
                          <option value="">Select reason...</option>
                          <option>Price</option>
                          <option>Competitor — Deloitte</option>
                          <option>Competitor — Accenture</option>
                          <option>Competitor — Other SI</option>
                          <option>No Decision</option>
                          <option>Timing</option>
                          <option>Relationship Gap</option>
                          <option>Capability Gap</option>
                          <option>Budget Eliminated</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* MEDDIC edit section */}
                  <MeddicSection isForm={true} formData={formData} onFormChange={handleFormChange} />
                </>
              )}
            </div>

            <div className="opp-modal-footer">
              {modalMode === 'view' ? (
                <>
                  <button className="opp-btn-secondary" onClick={() => handleEditClick(selectedOpp)}>Edit</button>
                  <button className="opp-btn-danger" onClick={() => setShowDeleteConfirm(true)}>Delete</button>
                </>
              ) : (
                <>
                  <button className="opp-btn-secondary" onClick={handleCloseModal}>Cancel</button>
                  <button className="opp-btn-primary" onClick={handleSave} disabled={saving || !formData.name.trim()}>
                    {saving ? '⏳ Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>

            {showDeleteConfirm && (
              <div className="opp-confirm-overlay">
                <div className="opp-confirm-box">
                  <h3>Delete Opportunity?</h3>
                  <p>This will permanently delete <strong>"{selectedOpp.name}"</strong>. This cannot be undone.</p>
                  <div className="opp-confirm-actions">
                    <button className="opp-btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                    <button className="opp-btn-danger" onClick={handleDelete} disabled={deleting}>
                      {deleting ? '⏳ Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PipelinePage;
